import { type Decimal, dec, toStorage, ZERO } from "./decimal";

/**
 * Invariant 1: inventory is an append-only log of signed deltas. On-hand is always derived.
 *
 * - `receive`  stock arrives from a supplier (+)
 * - `use`      consumed in production (-)
 * - `waste`    dumped, spoiled, spilled (-)
 * - `adjust`   a count correction (either sign)
 * - `transfer` a pair between locations that sums to zero
 * - `borrow`   from another brewery: + when it arrives, - when paid back
 * - `lend`     to another brewery: - when it leaves, + when it comes back
 * - `harvest`  yeast taken from a brew to pitch again (+)
 * - `merge`    a pair across two items made one, out of one and into the other, that sums to zero
 */
export const MOVEMENT_KINDS = [
  "receive",
  "use",
  "waste",
  "adjust",
  "transfer",
  "borrow",
  "lend",
  "harvest",
  "merge",
] as const;
export type MovementKind = (typeof MOVEMENT_KINDS)[number];

export const MOVEMENT_LABELS: Record<MovementKind, string> = {
  receive: "Received",
  use: "Used",
  waste: "Dumped",
  adjust: "Count correction",
  transfer: "Transfer",
  borrow: "Borrowed",
  lend: "Lent",
  harvest: "Harvested",
  merge: "Made one item",
};

export class MovementError extends Error {}

/** Sign rules for an original movement. A reversal carries the opposite sign of what it reverses. */
export function checkSign(kind: MovementKind, quantity: string | Decimal): void {
  const q = dec(quantity);
  if (q.eq(ZERO)) throw new MovementError("A movement of zero changes nothing");
  if (kind === "receive" && q.lt(ZERO)) throw new MovementError("A receipt must add stock");
  if (kind === "harvest" && q.lt(ZERO)) throw new MovementError("A harvest must add stock");
  if ((kind === "use" || kind === "waste") && q.gt(ZERO)) {
    throw new MovementError(`${MOVEMENT_LABELS[kind]} must remove stock`);
  }
}

/**
 * A physical count is absolute, but the log only takes deltas. The delta is taken against what the counter
 * was shown, not against the database at write time, so movements recorded concurrently elsewhere (another
 * device, an offline queue replaying) still apply on top instead of being silently erased by the count.
 */
export function countDelta(counted: string | Decimal, expected: string | Decimal): string {
  return toStorage(dec(counted).minus(dec(expected)));
}

export interface LotBalance {
  lotId: string;
  /** Canonical quantity of this lot at the location. */
  quantity: string;
  /** Oldest first. ISO date or timestamp; compared as strings. */
  receivedAt: string;
}

export interface Allocation {
  lotId: string;
  /** Negative: the amount taken from this lot. */
  quantity: string;
}

/**
 * Splits a removal across lots, oldest first, when the person did not pick a lot. Stock beyond what is on
 * hand is charged to the newest lot, taking it negative: the event happened, so it is recorded, and the
 * negative balance is what flags the count as wrong. Refusing it would lose a real event (see
 * docs/architecture.md, "Negative on-hand is allowed and flagged").
 */
export function allocateRemoval(
  amount: string | Decimal,
  lots: readonly LotBalance[],
): Allocation[] {
  let remaining = dec(amount);
  if (remaining.lte(ZERO)) throw new MovementError("Removal amount must be positive");
  if (lots.length === 0)
    throw new MovementError("There is no lot of this item here to take stock from");
  const ordered = [...lots].sort((a, b) =>
    a.receivedAt === b.receivedAt
      ? a.lotId.localeCompare(b.lotId)
      : a.receivedAt.localeCompare(b.receivedAt),
  );
  const allocations: Allocation[] = [];
  for (const lot of ordered) {
    if (remaining.eq(ZERO)) break;
    const available = dec(lot.quantity);
    if (available.lte(ZERO)) continue;
    const take = available.lt(remaining) ? available : remaining;
    allocations.push({ lotId: lot.lotId, quantity: toStorage(take.times("-1")) });
    remaining = remaining.minus(take);
  }
  if (remaining.gt(ZERO)) {
    const newest = ordered[ordered.length - 1] as LotBalance;
    const existing = allocations.find((a) => a.lotId === newest.lotId);
    if (existing) {
      existing.quantity = toStorage(dec(existing.quantity).minus(remaining));
    } else {
      allocations.push({ lotId: newest.lotId, quantity: toStorage(remaining.times("-1")) });
    }
  }
  return allocations;
}

export interface MovementLike {
  lotId: string;
  locationId: string;
  quantity: string;
}

/**
 * Reference projection: the database computes the same thing with SUM ... GROUP BY. Nothing calls it;
 * `@public` keeps knip from reporting it.
 * @public
 */
export function onHandByLotLocation(movements: Iterable<MovementLike>): Map<string, Decimal> {
  const totals = new Map<string, Decimal>();
  for (const m of movements) {
    const key = `${m.lotId}@${m.locationId}`;
    totals.set(key, (totals.get(key) ?? ZERO).plus(dec(m.quantity)));
  }
  return totals;
}
